import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import type IntlService from 'ember-intl/services/intl';

export interface AditoolsDialogSaveSignature {
  Element: HTMLDialogElement;
  Args: {
    title: string;
    save: (name: string, description: string) => void;
    close: () => void;
    designName?: string;
    designDesc?: string;
    designNameClass?: string;
    designDescClass?: string;
    closeButtonText?: string;
    saveButtonText?: string;
    dialogClass?: string;
    submit?: () => void; // Obsolete
  };
}
type AditoolsDialogSaveArgs = AditoolsDialogSaveSignature['Args'];

export default class AditoolsDialogSave extends Component<AditoolsDialogSaveSignature> {
  @service declare intl: IntlService;
  @tracked workingDesignName: string = this._designName;
  @tracked workingDesignDesc: string = this._designDesc;
  @tracked isDesignNameValid = true;
  @tracked isDesignDescValid = true;

  constructor(owner: unknown, args: AditoolsDialogSaveArgs) {
    super(owner, args);

    assert('`title` is required', !isNone(this.args.title));
    if (!isNone(this.args.submit)) {
      console.log(`
      The \`submit\` parameter has changed to \`save\`.
      Please adjust your usage accordingly.
      `);
    }
    assert('`save` is required', !isNone(this.args.save));
    assert('`close` is required', !isNone(this.args.close));
  }

  // Optional parameters with defaults applied
  get _designName() {
    return this.args.designName ?? '';
  }

  get _designDesc() {
    return this.args.designDesc ?? '';
  }

  get _designNameClass() {
    return this.args.designNameClass ?? '';
  }

  get _designDescClass() {
    return this.args.designDescClass ?? '';
  }

  get _closeButtonText() {
    return this.args.closeButtonText ?? this.intl.t('ember-aditools-page.common.cancel');
  }

  get _saveButtonText() {
    return this.args.saveButtonText ?? this.intl.t('ember-aditools-page.common.save');
  }

  @action onValueChanged(event: Event) {
    const fieldName: string = (<HTMLInputElement>event.target).id.toString();
    const value: string = (<HTMLInputElement>event.target).value.toString();
    if (fieldName === 'save-design-name-id') {
      if (this.workingDesignName !== value) {
        this.workingDesignName = value;
      }
    } else if (fieldName === 'save-design-desc-id') {
      if (this.workingDesignDesc !== value) {
        this.workingDesignDesc = value;
      }
    }
  }

  @action saveDesign(closeDialog: () => void) {
    if (this.validateDesignFields()) {
      this.args.save(this.workingDesignName, this.workingDesignDesc);
      closeDialog();
    }
  }

  validateDesignFields(this: AditoolsDialogSave) {
    const workingDesignName = this.workingDesignName ? this.workingDesignName.trim() : '';
    const workingDesignDesc = this.workingDesignDesc ? this.workingDesignDesc.trim() : '';
    this.isDesignNameValid = workingDesignName.length > 0;
    this.isDesignDescValid = workingDesignDesc.length > 0;
    return this.isDesignNameValid && this.isDesignDescValid;
  }
}
